import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../ui/Button';
import Card from '../../ui/Card';
import StatusBadge from '../../ui/Status/StatusBadge';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { finesService, errorMessage } from '../../services/finesService';
import { formatMoney } from '../../constants/finance';
import SalikCatchUp from './SalikCatchUp';
import { confirmDialog } from '../../ui/feedback';
import { useRowLinks } from '../TrafficFines/components/jobLinks';

/**
 * Automatic Ejar registration on the Parking tab (owner, 2 Oct 2026: "اى باركينج يظهر اتوماتيك يتسجل على ايجار ولو ايجار رفض
 * يحاول مرة واتنين وثلالثه فى اوقات مختلفة ولو بعد كده متسجلتش اعرف من ريبورت فى صفحة الباركينج"), on the Traffic Fines
 * page (kind 'fines' - "جهز للمخالفات زى الباركينج") and on the Salik tab (kind 'salik' - the month's one Salik line per
 * contract, "كل يوم الساعه 12 باليل بالظبط يتقفل اليوم ويتسجل سطر اليوم ده على ايجار"): whether it is on and from when, its
 * last pass, what waits for another try, and the report of what Ejar kept refusing. Each switched on / off in Admin >
 * Schedules by an admin; an admin may send a refused one back for another try - and, for the Salik line, look at what
 * one contract's line would be (a preview) and write it (a test on one or two while it is off).
 */
const wall = (t) => {
    const s = String(t || '');
    return s.length >= 16 && s[4] === '-' ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)} ${s.slice(11, 16)}` : (s || '—');
};
const day = (t) => (t && String(t).length >= 10 ? `${String(t).slice(8, 10)}/${String(t).slice(5, 7)}/${String(t).slice(0, 4)}` : '—');
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];   // the engine's 0 = Monday
const plural = (n, one) => `${n} ${n === 1 ? one : `${one}s`}`;

const KINDS = {
    parking: { one: 'parking', many: 'parkings', retry: (ids) => finesService.ejarAutoParkingRetry(ids) },
    fines: { one: 'fine', many: 'fines', retry: (ids) => finesService.ejarAutoFinesRetry(ids) },
    salik: { one: 'contract', many: 'Salik lines', retry: (ids) => finesService.ejarAutoSalikRetry(ids) },
};

/** The month's lines on Ejar now and the one line that takes their place - a preview's or a write's. */
function SalikLinePlan({ line }) {
    const n = line.new || {};
    const done = line.result === 'written';
    return (
        <div className="text-xs text-gray-800 flex flex-col gap-0.5">
            <div><span className="font-semibold">{line.month}</span> (up to {day(line.till)}):{' '}
                {line.result === 'skipped' ? <span className="text-gray-600">nothing to write - {line.skip}</span>
                    : line.result === 'failed' ? <span className="text-red-700">refused - {line.error}</span>
                        : done ? <span className="text-green-700">written{line.by ? ` as ${line.by}` : ''}</span>
                            : <span className="text-blue-800">would be written</span>}
            </div>
            {line.closed && line.owed != null && (
                <div className="pl-3 text-gray-600">closed contract: Tolls item {formatMoney(Number(line.tolls) || 0)} + lines hold {formatMoney(Number(line.ejarHolds) || 0)} of {formatMoney(Number(line.owed) || 0)}
                    {Number(line.short) > 0 ? ` - ${formatMoney(Number(line.short))} short` : ''}</div>
            )}
            {line.result !== 'skipped' && (line.old || []).map((o) => (
                <div key={o.id} className="pl-3 text-gray-600">
                    {done ? 'deleted' : 'deletes'}: <span className="font-mono">{o.comment}</span> · {formatMoney(Number(o.amount) || 0)} ·{' '}
                    {o.yelo ? 'written by yelo' : <span className="font-semibold text-amber-800">typed by {o.by || 'staff'}</span>}
                </div>
            ))}
            {line.new && (
                <div className="pl-3">
                    {done ? 'added' : 'adds'}: <span className="font-mono">{n.comment}</span> · {formatMoney(Number(n.amount) || 0)} + VAT
                    = {formatMoney(Number(n.final) || 0)} · {n.difference ? 'the difference to the Tolls item, to the fils'
                        : `${plural(n.trips, 'trip')} (${n.newTrips} new${n.late ? `, ${n.late} late` : ''})`}
                    {line.wrongAmount != null && ` · the line on Ejar holds ${formatMoney(Number(line.wrongAmount) || 0)}`}
                </div>
            )}
            {line.removedOnEjar > 0 && <div className="pl-3 text-gray-600">{plural(line.removedOnEjar, 'trip')} staff took off Ejar - left off</div>}
            {line.beforeStart > 0 && <div className="pl-3 text-gray-600">{plural(line.beforeStart, 'trip')} from before the automatic start day - left for a person</div>}
        </div>
    );
}

/** An admin's test on one contract: what its line would be, then - asked again - written. */
function SalikTest({ onDone }) {
    const [contract, setContract] = useState('');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const cn = contract.trim().toUpperCase();
    const run = async (write) => {
        if (!cn) return;
        setBusy(true); setErr(null);
        try {
            const r = await finesService.ejarAutoSalikRun({ contract: cn, write });
            const one = (r.results || [])[0] || null;
            // ``skipped`` is a sentence when the pass did not run (every contract is written - Invygo too)
            setErr((typeof r.skipped === 'string' && r.skipped) || r.error || (one && !(one.lines || []).length ? one.error : null) || null);
            setResult(one && { ...one, write });
            if (write) onDone();
        } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
    };
    const planned = result && !result.write && result.contract === cn && (result.lines || []).some((l) => l.result === 'planned');
    return (
        <div className="flex flex-col gap-1 border-t border-gray-200 pt-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <label htmlFor="salik-line-test" className="text-gray-700">One contract's Salik line:</label>
                <input id="salik-line-test" value={contract} onChange={(e) => { setContract(e.target.value); setResult(null); setErr(null); }}
                    placeholder="contract number" className="border border-gray-300 rounded px-2 py-0.5 font-mono text-sm w-56" />
                <Button size="xs" disabled={busy || !cn} onClick={() => run(false)}>Preview</Button>
                {planned && <Button size="xs" variant="danger" disabled={busy} onClick={async () => {
                    if (await confirmDialog({ title: `Write ${cn}'s Salik line on Ejar?`, message: "The previewed line replaces the contract's Salik line on Ejar now.", confirmLabel: 'Write it', danger: true })) run(true);
                }}>Write it on Ejar</Button>}
                {busy && <span className="text-xs text-gray-600">reading Ejar…</span>}
            </div>
            {err && <div role="alert" className="text-xs text-red-700">{err}</div>}
            {result && (result.lines || []).map((l) => <SalikLinePlan key={l.month} line={l} />)}
        </div>
    );
}

export default function AutoEjarCard({ kind = 'parking', isAdmin = false, refreshKey }) {
    const words = KINDS[kind] || KINDS.parking;
    const { FineLink, ContractLink, modals } = useRowLinks();   // a row's fine / contract opens in one click (job-table rule)
    const [data, setData] = useState(null);
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        try { setData((await finesService.ejarAuto())[kind] || null); } catch (e) {
            // a user without the report's permission simply does not see it
            setData(e?.response?.status === 403 ? null : { error: errorMessage(e) });
        }
    }, [kind]);
    useEffect(() => { load(); }, [load, refreshKey]);
    if (!data) return null;
    if (data.error) return <div role="alert" className="text-sm text-red-700">Automatic Ejar registration: {data.error}</div>;

    const last = (data.runs || []).find((r) => !r.dry);        // an admin's preview is no pass
    const on = !!(data.on && data.from);
    const salik = kind === 'salik';
    const retry = async (ids) => {
        setBusy(true); setMsg(null);
        try {
            const r = await words.retry(ids);
            setMsg({ ok: true, text: `${r.reset} ${words.one}(s) will be tried again on the next pass.` });
            await load();
        } catch (e) { setMsg({ ok: false, text: errorMessage(e) }); } finally { setBusy(false); }
    };
    const failed = data.failed || [];
    const replaced = (salik && last?.replaced) || [];
    return (
        <Card padding="none"><div className="px-4 py-3 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-gray-900">{salik ? 'Automatic Salik line on Ejar' : 'Automatic Ejar registration'}</span>
                <StatusBadge tone={on ? 'success' : 'neutral'} label={on ? 'on' : 'off'} />
                {on && salik && (
                    <span className="text-gray-600">one Salik &amp; Darb line per contract a month, trips from {day(data.from)} · daily contracts every day at {data.time},
                        monthly, leasing and Invygo {data.weekday < 0 ? 'by hand' : `on ${WEEKDAYS[data.weekday] || '—'}`} · up to {data.tries} tries, one a day
                        {data.lastRun && <> · last pass {wall(data.lastRun)}</>}{data.nextRun && <> · next {wall(data.nextRun)}</>}</span>
                )}
                {on && !salik && (
                    <span className="text-gray-600">{words.many} from {day(data.from)} · checked every {data.everyMin} min · up to {data.tries} tries, {data.retryHours} h apart
                        {data.lastRun && <> · last pass {wall(data.lastRun)}</>}{data.nextRun && <> · next {wall(data.nextRun)}</>}</span>
                )}
                {!on && (
                    <span className="text-gray-600">an admin turns it on in Admin &gt; Schedules (with the day it starts from)</span>
                )}
            </div>
            {last && kind === 'parking' && (
                <div className="text-xs text-gray-600">
                    last pass {wall(last.at)}: {last.written} written{last.duplicate ? `, ${last.duplicate} already on Ejar` : ''}{last.failed ? `, ${last.failed} refused` : ''}
                    {last.noContract ? `, ${last.noContract} on no contract (the company's)` : ''}{last.waiting ? `, ${last.waiting} waiting to try again` : ''}
                    {last.error && <span className="text-red-700"> - {last.error}</span>}
                </div>
            )}
            {last && kind === 'fines' && (
                <div className="text-xs text-gray-600">
                    last pass {wall(last.at)}: {last.registered} registered{last.failed ? `, ${last.failed} refused` : ''}
                    {last.waiting ? `, ${last.waiting} waiting to try again` : ''}
                    {last.left ? `, ${last.left} left for a person (${Object.entries(last.leftWhy || {}).map(([w, n]) => `${n} ${w}`).join(', ')})` : ''}
                    {last.error && <span className="text-red-700"> - {last.error}</span>}
                </div>
            )}
            {last && salik && (
                <div className="text-xs text-gray-600">
                    last pass {wall(last.at)} (up to {day(last.till)}): {last.written} written, {last.upToDate} up to date
                    {last.failed ? `, ${last.failed} refused` : ''}{last.givenUp ? `, ${last.givenUp} in the report below` : ''}
                    {last.notDue ? `, ${plural(last.notDue, 'monthly contract')} ${last.byHand || data.weekday < 0 ? 'left to write by hand' : `waiting for ${WEEKDAYS[data.weekday] || 'its day'}`}` : ''}
                    {last.error && <span className="text-red-700"> - {last.error}</span>}
                </div>
            )}
            {on && data.skipped && (
                <div className="text-xs text-amber-800">pass of {wall(data.skipped.at)} waited: {data.skipped.reason}</div>
            )}
            {(data.retrying || []).length > 0 && (
                <div className="text-xs text-amber-800">{data.retrying.length} {words.one}(s) refused by Ejar, waiting for another try</div>
            )}
            {msg && <div role={msg.ok ? 'status' : 'alert'} className={`text-sm font-medium ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</div>}
            {replaced.length > 0 && (
                <>
                    <div className="text-sm font-semibold text-amber-800">Lines staff typed, replaced on the last pass: {replaced.length}</div>
                    <TableContainer>
                        <Table sticky>
                            <TableHead><TableRow>
                                <TableHeader>#</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Month</TableHeader><TableHeader>Staff line</TableHeader>
                                <TableHeader>Typed by</TableHeader><TableHeader>Amount</TableHeader><TableHeader>Now on Ejar</TableHeader><TableHeader>Amount</TableHeader>
                            </TableRow></TableHead>
                            <TableBody>
                                {replaced.flatMap((x) => (x.old || []).map((o, i) => ({ x, o, i }))).map(({ x, o, i }, n) => (
                                    <TableRow key={`${x.contract}-${x.month}-${i}`}>
                                        <TableCell>{n + 1}</TableCell>
                                        <TableCell><ContractLink contract={x.contract} /></TableCell>
                                        <TableCell className="font-mono">{x.month}</TableCell>
                                        <TableCell><div className="font-mono" style={{ margin: '0 auto', whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 200, maxWidth: 300 }}>{o.comment}</div></TableCell>
                                        <TableCell>{o.by || '—'}</TableCell>
                                        <TableCell>{formatMoney(Number(o.amount) || 0)}</TableCell>
                                        <TableCell><div className="font-mono" style={{ margin: '0 auto', whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 240, maxWidth: 360 }}>{x.new?.comment}</div></TableCell>
                                        <TableCell>{formatMoney(Number(x.new?.amount) || 0)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            {failed.length > 0 && (
                <>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-semibold text-red-700">Not registered after {data.tries} tries: {failed.length}</span>
                        {isAdmin && <Button size="xs" disabled={busy} onClick={() => retry(failed.map((x) => x.id))}>Try all again</Button>}
                    </div>
                    <TableContainer>
                        <Table sticky>
                            <TableHead><TableRow>
                                <TableHeader>#</TableHeader>
                                {kind === 'fines' && <TableHeader>Ticket</TableHeader>}
                                {salik ? <><TableHeader>Contract</TableHeader><TableHeader>Up to</TableHeader></> : <>
                                    <TableHeader>Plate</TableHeader><TableHeader>{kind === 'fines' ? 'Date' : 'In → Out'}</TableHeader>
                                    <TableHeader>{kind === 'fines' ? 'Offense' : 'Place'}</TableHeader>
                                    <TableHeader>Amount</TableHeader><TableHeader>Contract</TableHeader></>}
                                <TableHeader>Last try</TableHeader>
                                <TableHeader>Why</TableHeader>{isAdmin && <TableHeader>Retry</TableHeader>}
                            </TableRow></TableHead>
                            <TableBody>
                                {failed.map((x, n) => (
                                    <TableRow key={x.id}>
                                        <TableCell>{n + 1}</TableCell>
                                        {kind === 'fines' && <TableCell><FineLink ticket={x.id} /></TableCell>}
                                        {salik ? <>
                                            <TableCell><ContractLink contract={x.contract || x.id} /></TableCell>
                                            <TableCell className="font-mono">{day(x.when)}</TableCell>
                                        </> : <>
                                            <TableCell className="font-mono">{x.plate}</TableCell>
                                            <TableCell className="font-mono">{kind === 'fines' ? wall(x.when) : <>{wall(x.entry)} → {wall(x.when).slice(11)}</>}</TableCell>
                                            <TableCell>{x.place}</TableCell>
                                            <TableCell>{formatMoney(Number(x.amount) || 0)}</TableCell>
                                            <TableCell>{x.contract ? <ContractLink contract={x.contract} plate={x.plate} /> : '—'}</TableCell>
                                        </>}
                                        <TableCell className="font-mono">{wall(x.last)}</TableCell>
                                        <TableCell><div style={{ margin: '0 auto', whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 200, maxWidth: 340 }}>{x.error}</div></TableCell>
                                        {isAdmin && <TableCell><Button size="xs" disabled={busy} onClick={() => retry([x.id])}>Try again</Button></TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            {salik && isAdmin && <SalikTest onDone={load} />}
            {salik && isAdmin && <SalikCatchUp />}
            {modals}
        </div></Card>
    );
}
